import { Pill } from "@/components/atoms/Pill/Pill";
import { Tag } from "@/components/molecules/Tag/Tag";
import { TagGroup } from "@/components/molecules/TagGroup/TagGroup";
import { t } from "@lingui/core/macro";
import { useMemo, useState } from "react";

import { Button } from "@/components/atoms/Button/Button";
import { TooltipWrapper } from "@/components/molecules/TooltipWrapper/TooltipWrapper";
import "./TagGroupWithAdd.scss";

type TagGroupWithAddProps = {
  onAdd: (value: string) => void;
  isAddAtEnd?: boolean;
  capacity?: number;
  tags: React.ReactNode[];
  initialVisibleCount?: number;
  isSideNav?: boolean;
};

export const TagGroupWithAdd: React.FC<TagGroupWithAddProps> = ({
  onAdd,
  isAddAtEnd = true,
  capacity = 20,
  tags,
  initialVisibleCount = 10,
  isSideNav,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const hasMoreThanInitial = tags.length > initialVisibleCount;

  const visibleTags = useMemo(() => {
    if (isExpanded) return tags;
    return tags.slice(0, initialVisibleCount);
  }, [tags, isExpanded, initialVisibleCount]);

  const generateNewTagLabel = () => {
    let index = 1;
    let label = `New Tag (${index})`;
    const existingLabels = tags
      .map((tag) => {
        if (tag && typeof tag === "object" && "key" in tag && tag.key != null) {
          return String(tag.key);
        }
        if (tag && typeof tag === "object" && "props" in tag && (tag as any).props.label) {
          return (tag as any).props.label;
        }
        return null;
      })
      .filter(Boolean);

    while (existingLabels.includes(label)) {
      index++;
      label = `New Tag (${index})`;
    }
    return label;
  };

  const AddControl = (
    <div className="tag-group__add-container">
      <TooltipWrapper tooltipText={t`Add tag`}>
        <Pill onClick={() => setIsAdding(true)} className="tag-style selected">
          <span className="material-icons">add</span>
        </Pill>
      </TooltipWrapper>
    </div>
  );

  const ShowMoreControl = hasMoreThanInitial && !isExpanded && (
    <div className="tag-group__show-more-container">
      <Button onClick={() => setIsExpanded(true)} buttonType={"clear"} size="small" className="no-padding">
        <span className="material-icons">keyboard_arrow_down</span>
        {t`Show more`}
      </Button>
    </div>
  );

  const ShowLessControl = hasMoreThanInitial && isExpanded && (
    <div className="tag-group__show-more-container">
      <Button onClick={() => setIsExpanded(false)} buttonType={"clear"} size="small" className="no-padding">
        <span className="material-icons">keyboard_arrow_up</span>
        {t`Show less`}
      </Button>
    </div>
  );

  return (
    <TagGroup>
      {!isAddAtEnd && tags.length < capacity && AddControl}
      {isSideNav ? visibleTags : tags}
      {isAddAtEnd && tags.length < capacity && AddControl}

      {isAdding && (
        <Tag
          label={generateNewTagLabel()}
          isEditMode={true}
          onRename={(value) => {
            onAdd(value);
            setIsAdding(false);
          }}
          onCancelEdit={() => setIsAdding(false)}
        />
      )}
      {isSideNav && ShowMoreControl}
      {isSideNav && ShowLessControl}
    </TagGroup>
  );
};
